import React, { useState, useEffect } from 'react';
import { getRequirementsList, addNewRequirement, updateExistingRequirement, getRequirementDetails, getOrganizationsList, deleteRequirementById } from '../services/api';
import JDParser from '../components/JDParser';
import ImportRequirementsModal from '../components/ImportRequirementsModal';
import '../styles/RequirementList.css';

/** Decode the JWT payload without verifying the signature (safe for client-side role detection) */
const decodeToken = () => {
  try {
    const token = localStorage.getItem('admin_token');
    if (!token) return {};
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
};

/** Inline field error — matches MNC form UX */
const FieldError = ({ msg }) =>
  msg ? (
    <span className="field-error-msg" role="alert">
      <svg className="field-error-icon" viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true">
        <path fillRule="evenodd" d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7-4a1 1 0 10-2 0v4a1 1 0 002 0V6zm-1 8a1.25 1.25 0 100-2.5A1.25 1.25 0 0010 14z" clipRule="evenodd" />
      </svg>
      {msg}
    </span>
  ) : null;

const RequirementList = () => {
  const [requirements, setRequirements] = useState([]);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const currentUser = decodeToken();
  const isSuperAdmin = currentUser.role === 'super_admin';

  const [form, setForm] = useState({
    organization_id: '',
    jobTitle: '',
    department: '',
    hiringManager: '',
    jobLocation: '',
    employmentType: '',
    salaryRange: '',
    experienceLevel: '',
    applicationDeadline: '',
    numberOfPositions: '',
    level: '',
    jobPostingDate: '',
    expectedStartDate: '',
    candidateQualifications: {
      minimumQualifications: { education: '', yearsOfExperience: '', certifications: [], degree: '' },
      preferredQualifications: { additionalCertifications: '', preferredExperience: '', languages: '', certifications: '' }
    },
    skillsAndCompetencies: {
      technicalSkills: [], softSkills: [], industrySpecificSkills: [], toolsAndTechnologies: [], competencyLevels: {}
    },
    jobResponsibilities: { primaryResponsibilities: [], secondaryResponsibilities: [], performanceMetrics: [] },
    interviewProcessDetails: {
      stagesOfInterview: { screening: '', technicalInterview: '', behavioralInterview: '', finalInterview: '', Round1: '', Round2: '' },
      interviewPanel: []
    },
    status: '',
    approvalProcess: { approvalLevel: '', approvers: [], approvalStatus: '', comments: '', approvedBy: '', steps: [] }
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRequirements, setFilteredRequirements] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showParser, setShowParser] = useState(false);
  const [autoFilledFields, setAutoFilledFields] = useState([]);

  const totalSteps = 4;

  useEffect(() => {
    fetchRequirements();
    if (isSuperAdmin) fetchOrganizations();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setFilteredRequirements(requirements.filter(req =>
        req.jobTitle.toLowerCase().includes(searchTerm.toLowerCase())
      ));
    } else {
      setFilteredRequirements(requirements);
    }
  }, [searchTerm, requirements]);

  const fetchRequirements = async () => {
    try {
      const data = await getRequirementsList();
      setRequirements(data);
      setFilteredRequirements(data);
    } catch (err) {
      console.error('Error fetching requirements:', err);
      setError('Failed to load requirements.');
    }
  };

  const fetchOrganizations = async () => {
    try {
      const data = await getOrganizationsList();
      setOrganizations(data);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  // ─── Validation ───────────────────────────────────────────────────────────────

  const validateStep = (step) => {
    const errors = {};
    if (step === 1) {
      if (isSuperAdmin && !form.organization_id)
        errors.organization_id = 'Please select an organization to continue.';
      if (!form.jobTitle?.trim())
        errors.jobTitle = 'Job title is required.';
      if (!form.department?.trim())
        errors.department = 'Department is required.';
      if (!form.hiringManager?.trim())
        errors.hiringManager = 'Hiring manager name is required.';
      if (!form.jobLocation?.trim())
        errors.jobLocation = 'Job location is required.';
      if (!form.employmentType)
        errors.employmentType = 'Please select an employment type.';
      if (!form.status)
        errors.status = 'Please select a status for this requirement.';
    }
    if (step === 3) {
      const validSkills = (form.skillsAndCompetencies.technicalSkills || []).filter(s => s.trim());
      if (validSkills.length === 0)
        errors.technicalSkills = 'Please add at least one technical skill.';
    }
    if (step === 4) {
      const validResps = (form.jobResponsibilities.primaryResponsibilities || []).filter(r => r.trim());
      if (validResps.length === 0)
        errors.primaryResponsibilities = 'Please add at least one primary responsibility.';
    }
    return errors;
  };

  const clearFieldError = (key) => {
    if (fieldErrors[key]) {
      setFieldErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector('.field-error-msg');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  // ─── Input handlers ───────────────────────────────────────────────────────────

  const handleInputChange = (e, field, subfield) => {
    clearFieldError(field);
    if (subfield) {
      setForm({ ...form, [field]: { ...form[field], [subfield]: e.target.value } });
    } else {
      setForm({ ...form, [field]: e.target.value });
    }
  };

  const handleNestedInputChange = (e, field, subfield, nestedField) => {
    setForm({
      ...form,
      [field]: { ...form[field], [subfield]: { ...form[field][subfield], [nestedField]: e.target.value } }
    });
  };

  const handleArrayChange = (e, field, subfield, index) => {
    clearFieldError(subfield);
    const updatedArray = [...form[field][subfield]];
    updatedArray[index] = e.target.value;
    setForm({ ...form, [field]: { ...form[field], [subfield]: updatedArray } });
  };

  const addArrayItem = (field, subfield) => {
    clearFieldError(subfield);
    setForm({ ...form, [field]: { ...form[field], [subfield]: [...form[field][subfield], ''] } });
  };

  const removeArrayItem = (field, subfield, index) => {
    const updatedArray = [...form[field][subfield]];
    updatedArray.splice(index, 1);
    setForm({ ...form, [field]: { ...form[field], [subfield]: updatedArray } });
  };

  const handlePanelMemberChange = (e, field, subfield, index, prop) => {
    const updatedArray = [...form[field][subfield]];
    updatedArray[index] = { ...updatedArray[index], [prop]: e.target.value };
    setForm({ ...form, [field]: { ...form[field], [subfield]: updatedArray } });
  };

  const addPanelMember = (field, subfield, template = { name: '', email: '', phone: '' }) => {
    setForm({ ...form, [field]: { ...form[field], [subfield]: [...form[field][subfield], template] } });
  };

  // ─── Step navigation ──────────────────────────────────────────────────────────

  const nextStep = () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      scrollToFirstError();
      return;
    }
    setFieldErrors({});
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setFieldErrors({});
    setCurrentStep(currentStep - 1);
  };

  // ─── CRUD handlers ────────────────────────────────────────────────────────────

  const handleEdit = async (id) => {
    try {
      const req = await getRequirementDetails(id);
      const normalizedReq = {
        ...req,
        organization_id: req.organization_id || '',
        skillsAndCompetencies: {
          technicalSkills: [], softSkills: [], industrySpecificSkills: [], toolsAndTechnologies: [], competencyLevels: {},
          ...(req.skillsAndCompetencies || {}),
        },
        candidateQualifications: {
          minimumQualifications: {
            education: '', yearsOfExperience: '', certifications: [], degree: '',
            ...((req.candidateQualifications || {}).minimumQualifications || {}),
          },
          preferredQualifications: {
            additionalCertifications: '', preferredExperience: '', languages: '', certifications: '',
            ...((req.candidateQualifications || {}).preferredQualifications || {}),
          },
        },
        jobResponsibilities: {
          primaryResponsibilities: [], secondaryResponsibilities: [], performanceMetrics: [],
          ...(req.jobResponsibilities || {}),
        },
        interviewProcessDetails: {
          stagesOfInterview: {
            screening: '', technicalInterview: '', behavioralInterview: '', finalInterview: '', Round1: '', Round2: '',
            ...((req.interviewProcessDetails || {}).stagesOfInterview || {}),
          },
          interviewPanel: [],
          ...(req.interviewProcessDetails || {}),
        },
        approvalProcess: {
          approvalLevel: '', approvers: [], approvalStatus: '', comments: '', approvedBy: '', steps: [],
          ...(req.approvalProcess || {}),
        },
      };
      setForm(normalizedReq);
      setSelectedRequirement(req);
      setIsEditing(true);
      setCurrentStep(1);
      setFieldErrors({});
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error fetching requirement details:', err);
      setError('Failed to fetch requirement details.');
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete requirement "${title}"? This action cannot be undone.`)) return;
    try {
      await deleteRequirementById(id);
      setRequirements(requirements.filter((r) => r._id !== id));
    } catch (err) {
      console.error('Error deleting requirement:', err);
      setError('Failed to delete requirement.');
    }
  };

  const handleAddNew = () => {
    resetForm();
    setIsEditing(false);
    setCurrentStep(1);
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      scrollToFirstError();
      return;
    }

    const toStr = (v) => (v == null ? '' : String(v));
    const toStrArray = (arr) =>
      Array.isArray(arr)
        ? arr.map(item =>
            item && typeof item === 'object'
              ? toStr(item.name || item.skill || item.value || JSON.stringify(item))
              : toStr(item)
          ).filter(Boolean)
        : [];

    const sanitizedSkills = {
      technicalSkills: toStrArray(form.skillsAndCompetencies?.technicalSkills),
      softSkills: toStrArray(form.skillsAndCompetencies?.softSkills),
      industrySpecificSkills: toStrArray(form.skillsAndCompetencies?.industrySpecificSkills),
      toolsAndTechnologies: toStrArray(form.skillsAndCompetencies?.toolsAndTechnologies),
      competencyLevels: Object.fromEntries(
        Object.entries(form.skillsAndCompetencies?.competencyLevels || {}).map(([k, v]) => [toStr(k), toStr(v)])
      ),
    };

    const formattedForm = {
      ...form,
      organization_id: form.organization_id || currentUser.organization_id || undefined,
      numberOfPositions: parseInt(form.numberOfPositions) || 0,
      applicationDeadline: form.applicationDeadline ? new Date(form.applicationDeadline).toISOString() : null,
      jobPostingDate: form.jobPostingDate ? new Date(form.jobPostingDate).toISOString() : null,
      expectedStartDate: form.expectedStartDate ? new Date(form.expectedStartDate).toISOString() : null,
      skillsAndCompetencies: sanitizedSkills,
    };

    try {
      if (isEditing && selectedRequirement) {
        await updateExistingRequirement(selectedRequirement._id, formattedForm);
      } else {
        await addNewRequirement(formattedForm);
      }
      fetchRequirements();
      resetForm();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving requirement:', err);
      const detail = err.response?.data?.detail;
      setError(
        detail
          ? `Save failed: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`
          : 'Failed to save requirement.'
      );
    }
  };

  const resetForm = () => {
    setForm({
      organization_id: '',
      jobTitle: '',
      department: '',
      hiringManager: '',
      jobLocation: '',
      employmentType: '',
      salaryRange: '',
      experienceLevel: '',
      applicationDeadline: '',
      numberOfPositions: '',
      level: '',
      jobPostingDate: '',
      expectedStartDate: '',
      candidateQualifications: {
        minimumQualifications: { education: '', yearsOfExperience: '', certifications: [], degree: '' },
        preferredQualifications: { additionalCertifications: '', preferredExperience: '', languages: '', certifications: '' }
      },
      skillsAndCompetencies: {
        technicalSkills: [], softSkills: [], industrySpecificSkills: [], toolsAndTechnologies: [], competencyLevels: {}
      },
      jobResponsibilities: { primaryResponsibilities: [], secondaryResponsibilities: [], performanceMetrics: [] },
      interviewProcessDetails: {
        stagesOfInterview: { screening: '', technicalInterview: '', behavioralInterview: '', finalInterview: '', Round1: '', Round2: '' },
        interviewPanel: []
      },
      status: '',
      approvalProcess: { approvalLevel: '', approvers: [], approvalStatus: '', comments: '', approvedBy: '', steps: [] }
    });
    setSelectedRequirement(null);
    setIsEditing(false);
    setError('');
    setFieldErrors({});
    setCurrentStep(1);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleParsedData = (parsedData, fieldsAutoFilled) => {
    setForm(prevForm => ({ ...prevForm, ...parsedData }));
    setAutoFilledFields(fieldsAutoFilled);
    setShowParser(false);
  };

  // ─── Step renderers ───────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <div className="form-step">
      <h3>Basic Job Details</h3>

      <div className={`form-group ${fieldErrors.jobTitle ? 'has-error' : ''}`}>
        <label>
          Job Title: <span className="required-star">*</span>
          <input
            type="text"
            value={form.jobTitle}
            onChange={(e) => handleInputChange(e, 'jobTitle')}
            placeholder="e.g. Senior Software Engineer"
            aria-invalid={!!fieldErrors.jobTitle}
          />
        </label>
        <FieldError msg={fieldErrors.jobTitle} />
      </div>

      <div className={`form-group ${fieldErrors.department ? 'has-error' : ''}`}>
        <label>
          Department: <span className="required-star">*</span>
          <input
            type="text"
            value={form.department}
            onChange={(e) => handleInputChange(e, 'department')}
            placeholder="e.g. Engineering"
            aria-invalid={!!fieldErrors.department}
          />
        </label>
        <FieldError msg={fieldErrors.department} />
      </div>

      <div className={`form-group ${fieldErrors.hiringManager ? 'has-error' : ''}`}>
        <label>
          Hiring Manager: <span className="required-star">*</span>
          <input
            type="text"
            value={form.hiringManager}
            onChange={(e) => handleInputChange(e, 'hiringManager')}
            placeholder="Full name"
            aria-invalid={!!fieldErrors.hiringManager}
          />
        </label>
        <FieldError msg={fieldErrors.hiringManager} />
      </div>

      <div className={`form-group ${fieldErrors.jobLocation ? 'has-error' : ''}`}>
        <label>
          Job Location: <span className="required-star">*</span>
          <input
            type="text"
            value={form.jobLocation}
            onChange={(e) => handleInputChange(e, 'jobLocation')}
            placeholder="e.g. Remote, New York, NY"
            aria-invalid={!!fieldErrors.jobLocation}
          />
        </label>
        <FieldError msg={fieldErrors.jobLocation} />
      </div>

      <div className={`form-group ${fieldErrors.employmentType ? 'has-error' : ''}`}>
        <label>
          Employment Type: <span className="required-star">*</span>
          <select
            value={form.employmentType}
            onChange={(e) => handleInputChange(e, 'employmentType')}
            aria-invalid={!!fieldErrors.employmentType}
          >
            <option value="">Select Type</option>
            <option value="Full-time">Full-time</option>
            <option value="Part-time">Part-time</option>
            <option value="Contract">Contract</option>
            <option value="Temporary">Temporary</option>
            <option value="Internship">Internship</option>
          </select>
        </label>
        <FieldError msg={fieldErrors.employmentType} />
      </div>

      <div className="form-group">
        <label>
          Salary Range:
          <input
            type="text"
            value={form.salaryRange}
            onChange={(e) => handleInputChange(e, 'salaryRange')}
            placeholder="e.g. $80,000 – $120,000"
          />
        </label>
      </div>

      <div className="form-group">
        <label>
          Experience Level:
          <select value={form.experienceLevel} onChange={(e) => handleInputChange(e, 'experienceLevel')}>
            <option value="">Select Level</option>
            <option value="Entry-level">Entry-level</option>
            <option value="Mid-level">Mid-level</option>
            <option value="Mid">Mid</option>
            <option value="Senior">Senior</option>
            <option value="Executive">Executive</option>
          </select>
        </label>
      </div>

      <div className="form-group">
        <label>
          Level (Job Grade):
          <select
            value={form.level}
            onChange={(e) => handleInputChange(e, 'level')}
            className={autoFilledFields.includes('level') ? 'auto-filled' : ''}
          >
            <option value="">Select Level (Optional)</option>
            <option value="L1">L1 - Entry Level</option>
            <option value="L2">L2 - Junior</option>
            <option value="L3">L3 - Mid Level</option>
            <option value="L4">L4 - Senior</option>
            <option value="L5">L5 - Staff/Principal</option>
            <option value="L6">L6 - Director/VP</option>
          </select>
        </label>
      </div>

      <div className="form-group">
        <label>
          Application Deadline:
          <input
            type="date"
            value={form.applicationDeadline ? form.applicationDeadline.split('T')[0] : ''}
            onChange={(e) => handleInputChange(e, 'applicationDeadline')}
          />
        </label>
      </div>

      <div className="form-group">
        <label>
          Number of Positions:
          <input
            type="number"
            value={form.numberOfPositions}
            onChange={(e) => handleInputChange(e, 'numberOfPositions')}
            min="1"
            placeholder="1"
          />
        </label>
      </div>

      <div className="form-group">
        <label>
          Job Posting Date:
          <input
            type="date"
            value={form.jobPostingDate ? form.jobPostingDate.split('T')[0] : ''}
            onChange={(e) => handleInputChange(e, 'jobPostingDate')}
          />
        </label>
      </div>

      <div className="form-group">
        <label>
          Expected Start Date:
          <input
            type="date"
            value={form.expectedStartDate ? form.expectedStartDate.split('T')[0] : ''}
            onChange={(e) => handleInputChange(e, 'expectedStartDate')}
          />
        </label>
      </div>

      <div className={`form-group ${fieldErrors.status ? 'has-error' : ''}`}>
        <label>
          Status: <span className="required-star">*</span>
          <select
            value={form.status}
            onChange={(e) => handleInputChange(e, 'status')}
            aria-invalid={!!fieldErrors.status}
          >
            <option value="">Select Status</option>
            <option value="Draft">Draft</option>
            <option value="Open">Open</option>
            <option value="In Review">In Review</option>
            <option value="Filled">Filled</option>
            <option value="Closed">Closed</option>
          </select>
        </label>
        <FieldError msg={fieldErrors.status} />
      </div>

      <div className="form-navigation">
        <button type="button" onClick={nextStep}>Next</button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="form-step">
      <h3>Candidate Qualifications</h3>

      <h4>Minimum Qualifications</h4>
      <div className="form-group">
        <label>
          Education:
          <input
            type="text"
            value={form.candidateQualifications.minimumQualifications.education || ''}
            onChange={(e) => handleNestedInputChange(e, 'candidateQualifications', 'minimumQualifications', 'education')}
          />
        </label>
      </div>
      <div className="form-group">
        <label>
          Degree:
          <input
            type="text"
            value={form.candidateQualifications.minimumQualifications.degree || ''}
            onChange={(e) => handleNestedInputChange(e, 'candidateQualifications', 'minimumQualifications', 'degree')}
          />
        </label>
      </div>
      <div className="form-group">
        <label>
          Years of Experience:
          <input
            type="text"
            value={form.candidateQualifications.minimumQualifications.yearsOfExperience || ''}
            onChange={(e) => handleNestedInputChange(e, 'candidateQualifications', 'minimumQualifications', 'yearsOfExperience')}
            placeholder="e.g. 3+ years"
          />
        </label>
      </div>

      <h4>Required Certifications</h4>
      {Array.isArray(form.candidateQualifications.minimumQualifications.certifications) &&
        form.candidateQualifications.minimumQualifications.certifications.map((cert, index) => (
          <div key={`cert-${index}`} className="form-group array-item">
            <input
              type="text"
              value={cert}
              onChange={(e) => {
                const updatedCerts = [...form.candidateQualifications.minimumQualifications.certifications];
                updatedCerts[index] = e.target.value;
                setForm({
                  ...form,
                  candidateQualifications: {
                    ...form.candidateQualifications,
                    minimumQualifications: { ...form.candidateQualifications.minimumQualifications, certifications: updatedCerts }
                  }
                });
              }}
            />
            <button type="button" onClick={() => {
              const updatedCerts = [...form.candidateQualifications.minimumQualifications.certifications];
              updatedCerts.splice(index, 1);
              setForm({
                ...form,
                candidateQualifications: {
                  ...form.candidateQualifications,
                  minimumQualifications: { ...form.candidateQualifications.minimumQualifications, certifications: updatedCerts }
                }
              });
            }}>Remove</button>
          </div>
        ))
      }
      <button type="button" onClick={() => {
        setForm({
          ...form,
          candidateQualifications: {
            ...form.candidateQualifications,
            minimumQualifications: {
              ...form.candidateQualifications.minimumQualifications,
              certifications: [...(form.candidateQualifications.minimumQualifications.certifications || []), '']
            }
          }
        });
      }}>Add Certification</button>

      <h4>Preferred Qualifications</h4>
      <div className="form-group">
        <label>
          Additional Certifications:
          <input type="text" value={form.candidateQualifications.preferredQualifications.additionalCertifications || ''} onChange={(e) => handleNestedInputChange(e, 'candidateQualifications', 'preferredQualifications', 'additionalCertifications')} />
        </label>
      </div>
      <div className="form-group">
        <label>
          Certifications:
          <input type="text" value={form.candidateQualifications.preferredQualifications.certifications || ''} onChange={(e) => handleNestedInputChange(e, 'candidateQualifications', 'preferredQualifications', 'certifications')} />
        </label>
      </div>
      <div className="form-group">
        <label>
          Preferred Experience:
          <input type="text" value={form.candidateQualifications.preferredQualifications.preferredExperience || ''} onChange={(e) => handleNestedInputChange(e, 'candidateQualifications', 'preferredQualifications', 'preferredExperience')} />
        </label>
      </div>
      <div className="form-group">
        <label>
          Languages:
          <input type="text" value={form.candidateQualifications.preferredQualifications.languages || ''} onChange={(e) => handleNestedInputChange(e, 'candidateQualifications', 'preferredQualifications', 'languages')} />
        </label>
      </div>

      <div className="form-navigation">
        <button type="button" onClick={prevStep}>Previous</button>
        <button type="button" onClick={nextStep}>Next</button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-step">
      <h3>Skills and Competencies</h3>

      <h4>Technical Skills <span className="required-star">*</span></h4>
      {form.skillsAndCompetencies.technicalSkills.map((skill, index) => (
        <div key={`tech-skill-${index}`} className="form-group array-item">
          <input
            type="text"
            value={skill}
            onChange={(e) => handleArrayChange(e, 'skillsAndCompetencies', 'technicalSkills', index)}
            placeholder="e.g. React, Python, SQL"
          />
          <button type="button" onClick={() => removeArrayItem('skillsAndCompetencies', 'technicalSkills', index)}>Remove</button>
        </div>
      ))}
      <button type="button" onClick={() => addArrayItem('skillsAndCompetencies', 'technicalSkills')}>Add Technical Skill</button>
      <FieldError msg={fieldErrors.technicalSkills} />

      <h4>Soft Skills</h4>
      {form.skillsAndCompetencies.softSkills.map((skill, index) => (
        <div key={`soft-skill-${index}`} className="form-group array-item">
          <input type="text" value={skill} onChange={(e) => handleArrayChange(e, 'skillsAndCompetencies', 'softSkills', index)} />
          <button type="button" onClick={() => removeArrayItem('skillsAndCompetencies', 'softSkills', index)}>Remove</button>
        </div>
      ))}
      <button type="button" onClick={() => addArrayItem('skillsAndCompetencies', 'softSkills')}>Add Soft Skill</button>

      <h4>Industry Specific Skills</h4>
      {form.skillsAndCompetencies.industrySpecificSkills.map((skill, index) => (
        <div key={`industry-skill-${index}`} className="form-group array-item">
          <input type="text" value={skill} onChange={(e) => handleArrayChange(e, 'skillsAndCompetencies', 'industrySpecificSkills', index)} />
          <button type="button" onClick={() => removeArrayItem('skillsAndCompetencies', 'industrySpecificSkills', index)}>Remove</button>
        </div>
      ))}
      <button type="button" onClick={() => addArrayItem('skillsAndCompetencies', 'industrySpecificSkills')}>Add Industry Skill</button>

      <h4>Tools and Technologies</h4>
      {form.skillsAndCompetencies.toolsAndTechnologies.map((tool, index) => (
        <div key={`tool-${index}`} className="form-group array-item">
          <input type="text" value={tool} onChange={(e) => handleArrayChange(e, 'skillsAndCompetencies', 'toolsAndTechnologies', index)} />
          <button type="button" onClick={() => removeArrayItem('skillsAndCompetencies', 'toolsAndTechnologies', index)}>Remove</button>
        </div>
      ))}
      <button type="button" onClick={() => addArrayItem('skillsAndCompetencies', 'toolsAndTechnologies')}>Add Tool/Technology</button>

      <h4>Competency Levels</h4>
      {form.skillsAndCompetencies.technicalSkills.map((skill, index) => (
        <div key={`comp-${index}`} className="form-group">
          <label>
            {skill} Proficiency:
            <select
              value={(form.skillsAndCompetencies.competencyLevels || {})[skill] || ''}
              onChange={(e) => {
                const updatedLevels = { ...(form.skillsAndCompetencies.competencyLevels || {}), [skill]: e.target.value };
                setForm({ ...form, skillsAndCompetencies: { ...form.skillsAndCompetencies, competencyLevels: updatedLevels } });
              }}
            >
              <option value="">Select Level</option>
              <option value="Beginner">Beginner</option>
              <option value="Beginner-Intermediate">Beginner-Intermediate</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Intermediate-Advanced">Intermediate-Advanced</option>
              <option value="Advanced">Advanced</option>
              <option value="Expert">Expert</option>
            </select>
          </label>
        </div>
      ))}

      <div className="form-navigation">
        <button type="button" onClick={prevStep}>Previous</button>
        <button type="button" onClick={nextStep}>Next</button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="form-step">
      <h3>Job Responsibilities</h3>

      <h4>Primary Responsibilities <span className="required-star">*</span></h4>
      {form.jobResponsibilities.primaryResponsibilities.map((resp, index) => (
        <div key={`primary-resp-${index}`} className="form-group array-item">
          <input
            type="text"
            value={resp}
            onChange={(e) => handleArrayChange(e, 'jobResponsibilities', 'primaryResponsibilities', index)}
            placeholder="Describe a key responsibility"
          />
          <button type="button" onClick={() => removeArrayItem('jobResponsibilities', 'primaryResponsibilities', index)}>Remove</button>
        </div>
      ))}
      <button type="button" onClick={() => addArrayItem('jobResponsibilities', 'primaryResponsibilities')}>Add Primary Responsibility</button>
      <FieldError msg={fieldErrors.primaryResponsibilities} />

      <h4>Secondary Responsibilities</h4>
      {form.jobResponsibilities.secondaryResponsibilities.map((resp, index) => (
        <div key={`secondary-resp-${index}`} className="form-group array-item">
          <input type="text" value={resp} onChange={(e) => handleArrayChange(e, 'jobResponsibilities', 'secondaryResponsibilities', index)} />
          <button type="button" onClick={() => removeArrayItem('jobResponsibilities', 'secondaryResponsibilities', index)}>Remove</button>
        </div>
      ))}
      <button type="button" onClick={() => addArrayItem('jobResponsibilities', 'secondaryResponsibilities')}>Add Secondary Responsibility</button>

      <h4>Performance Metrics</h4>
      {form.jobResponsibilities.performanceMetrics.map((metric, index) => (
        <div key={`metric-${index}`} className="form-group array-item">
          <input type="text" value={metric} onChange={(e) => handleArrayChange(e, 'jobResponsibilities', 'performanceMetrics', index)} />
          <button type="button" onClick={() => removeArrayItem('jobResponsibilities', 'performanceMetrics', index)}>Remove</button>
        </div>
      ))}
      <button type="button" onClick={() => addArrayItem('jobResponsibilities', 'performanceMetrics')}>Add Performance Metric</button>

      <div className="form-navigation">
        <button type="button" onClick={prevStep}>Previous</button>
        <button type="button" onClick={handleSave}>Save Requirement</button>
      </div>
    </div>
  );

  // Steps 5–7: unreachable (totalSteps = 4), kept as stubs
  const renderStep5 = () => <div className="form-step"><h3>Behavioral and Cultural Fit</h3></div>;

  const renderStep6 = () => (
    <div className="form-step">
      <h3>Interview Process Details</h3>
      <h4>Stages of Interview</h4>
      {['screening', 'technicalInterview', 'behavioralInterview', 'finalInterview', 'Round1', 'Round2'].map(stage => (
        <div className="form-group" key={stage}>
          <label>
            {stage.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}:
            <input
              type="text"
              value={form.interviewProcessDetails.stagesOfInterview[stage] || ''}
              onChange={(e) => setForm({ ...form, interviewProcessDetails: { ...form.interviewProcessDetails, stagesOfInterview: { ...form.interviewProcessDetails.stagesOfInterview, [stage]: e.target.value } } })}
            />
          </label>
        </div>
      ))}
      <h4>Interview Panel</h4>
      {form.interviewProcessDetails.interviewPanel.map((member, index) => (
        <div key={`panel-${index}`} className="form-group nested-form">
          <input type="text" placeholder="Name" value={member.name || ''} onChange={(e) => handlePanelMemberChange(e, 'interviewProcessDetails', 'interviewPanel', index, 'name')} />
          <input type="email" placeholder="Email" value={member.email || ''} onChange={(e) => handlePanelMemberChange(e, 'interviewProcessDetails', 'interviewPanel', index, 'email')} />
          <input type="tel" placeholder="Phone" value={member.phone || ''} onChange={(e) => handlePanelMemberChange(e, 'interviewProcessDetails', 'interviewPanel', index, 'phone')} />
          <button type="button" onClick={() => {
            const updatedPanel = [...form.interviewProcessDetails.interviewPanel];
            updatedPanel.splice(index, 1);
            setForm({ ...form, interviewProcessDetails: { ...form.interviewProcessDetails, interviewPanel: updatedPanel } });
          }}>Remove</button>
        </div>
      ))}
      <button type="button" onClick={() => addPanelMember('interviewProcessDetails', 'interviewPanel')}>Add Panel Member</button>
      <div className="form-navigation">
        <button type="button" onClick={prevStep}>Previous</button>
        <button type="button" onClick={nextStep}>Next</button>
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="form-step">
      <h3>Approval Process</h3>
      <div className="form-group">
        <label>Approval Level:
          <select value={form.approvalProcess.approvalLevel} onChange={(e) => handleInputChange(e, 'approvalProcess', 'approvalLevel')}>
            <option value="">Select Level</option>
            <option value="Department Head">Department Head</option>
            <option value="HR Manager">HR Manager</option>
            <option value="Director">Director</option>
            <option value="Executive">Executive</option>
          </select>
        </label>
      </div>
      <div className="form-group">
        <label>Approval Status:
          <select value={form.approvalProcess.approvalStatus} onChange={(e) => handleInputChange(e, 'approvalProcess', 'approvalStatus')}>
            <option value="">Select Status</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </label>
      </div>
      <div className="form-group">
        <label>Comments: <textarea value={form.approvalProcess.comments} onChange={(e) => handleInputChange(e, 'approvalProcess', 'comments')} rows="3" /></label>
      </div>
      <div className="form-group">
        <label>Approved By: <input type="text" value={form.approvalProcess.approvedBy} onChange={(e) => handleInputChange(e, 'approvalProcess', 'approvedBy')} /></label>
      </div>
      <div className="form-navigation">
        <button type="button" onClick={prevStep}>Previous</button>
        <button type="button" onClick={handleSave}>Save Requirement</button>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      case 7: return renderStep7();
      default: return renderStep1();
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="requirement-list-container">
      <h2>Job Requirements</h2>

      {error && <div className="error-message">{error}</div>}

      <div className="search-and-add">
        <input
          type="text"
          placeholder="Search by job title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <button onClick={() => setShowImport(true)} className="add-button" style={{ background: '#27272a', border: '1px solid #3f3f46' }}>
          Import from source
        </button>
        <button onClick={handleAddNew} className="add-button">Add New Requirement</button>
      </div>

      {showImport && (
        <ImportRequirementsModal
          onClose={() => setShowImport(false)}
          onImported={fetchRequirements}
        />
      )}

      <div className="requirements-table">
        <table>
          <thead>
            <tr>
              <th>Job Title</th>
              <th>Department</th>
              <th>Location</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequirements.map((req) => (
              <tr key={req._id}>
                <td>{req.jobTitle}</td>
                <td>{req.department}</td>
                <td>{req.jobLocation}</td>
                <td>{req.status}</td>
                <td style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => handleEdit(req._id)} className="edit-button">Edit</button>
                  <button
                    onClick={() => handleDelete(req._id, req.jobTitle)}
                    style={{ background: '#ef4444', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={closeModal}>&times;</span>
            <h2>{isEditing ? 'Edit Job Requirement' : 'Add New Job Requirement'}</h2>

            {isSuperAdmin && (
              <div className={`form-group ${fieldErrors.organization_id ? 'has-error' : ''}`} style={{ marginBottom: '16px' }}>
                <label>
                  Organization: <span className="required-star">*</span>
                  <select
                    value={form.organization_id}
                    onChange={(e) => {
                      clearFieldError('organization_id');
                      setForm({ ...form, organization_id: e.target.value });
                    }}
                    aria-invalid={!!fieldErrors.organization_id}
                  >
                    <option value="">— Select Organization —</option>
                    {organizations.map((org) => (
                      <option key={org._id} value={org._id}>
                        {org.organizationName || org.organization_id || org._id}
                      </option>
                    ))}
                  </select>
                </label>
                <FieldError msg={fieldErrors.organization_id} />
              </div>
            )}

            {!isEditing && !showParser && (
              <button
                type="button"
                className="parse-jd-btn"
                onClick={() => setShowParser(true)}
                style={{ marginBottom: '16px', padding: '10px 20px', background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                📄 Parse from Job Description
              </button>
            )}

            {showParser && (
              <JDParser onParsed={handleParsedData} onClose={() => setShowParser(false)} />
            )}

            {!showParser && (
              <>
                <div className="progress-bar">
                  <div className="progress" style={{ width: `${(currentStep / totalSteps) * 100}%` }} />
                </div>
                <div className="step-indicator">Step {currentStep} of {totalSteps}</div>
                <form onSubmit={handleSave}>
                  {renderStepContent()}
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RequirementList;